import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from 'react-oidc-context';

import { AnimatedSpinner } from '@components/AnimatedSpinner';
import { BillingProvider } from 'src/api/subscription';
import {
  CreditCardIcon,
  FileTextIcon,
  GearIcon,
  TagIcon,
} from '../../../../components/Icons';
import {
  useCustomerPortalLink,
  useRecurlyCustomerPortalUrl,
} from '../../../../hooks/apiHooks/subscriptionHooks';

interface BillingOptionProps {
  icon: React.ReactNode;
  title: string;
  description: string;
  link: string;
  external?: boolean;
}

function BillingOption({
  icon,
  title,
  description,
  link,
  external,
}: BillingOptionProps) {
  const extraAttributes = external && link ? { target: '_blank' } : {};
  return (
    <a
      className="flex items-center gap-4 p-4 rounded-lg transition-colors w-full text-left hover:bg-black hover:bg-opacity-20"
      href={link || '#'}
      rel="external"
      {...extraAttributes}>
      <div className="p-2 bg- icon-container ">{icon}</div>
      <div>
        <h4 className="font-medium text-white">{title}</h4>
        <p className="text-sm text-gray-400">{description}</p>
      </div>
    </a>
  );
}

export default function BillingOptions({
  billingProvider,
}: {
  billingProvider: BillingProvider;
}) {
  const { t } = useTranslation();
  const [isTokenExpired, setIsTokenExpired] = useState(false);
  const timerId = useRef<number | null>(null);
  const { user } = useAuth();
  const {
    data,
    isError: isCustomerLinkError,
    error: customerLinkError,
    refetch,
  } = useCustomerPortalLink({ token: user?.access_token || '' });

  const { data: recurlyCustomerPortalUrlData, isFetching: isRecurlyFetching } =
    useRecurlyCustomerPortalUrl(billingProvider === 'recurly');

  const recurlyUrl = recurlyCustomerPortalUrlData?.data.url;

  useEffect(() => {
    if (user?.access_token && !isTokenExpired) {
      void refetch();
      timerId.current = window.setInterval(() => {
        void refetch();
      }, 2 * 60000);
      return;
    }
    return () => {
      if (timerId.current) clearInterval(timerId.current);
    };
  }, [user?.access_token, isTokenExpired]);

  useEffect(() => {
    if (isCustomerLinkError) {
      if (customerLinkError.status === 401) {
        setIsTokenExpired(true);
        if (timerId.current) clearInterval(timerId.current);
        timerId.current = null;
      }
    }
  }, [isCustomerLinkError, customerLinkError]);

  if (isRecurlyFetching) {
    return (
      <div className="flex align-middle">
        <AnimatedSpinner />
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <BillingOption
        icon={<CreditCardIcon className="w-6 h-6 text-white" />}
        title={t('account.paymentMethods')}
        description={t('account.paymentMethodsDesc')}
        link={recurlyUrl || data?.data?.url || ''}
        external
      />
      <BillingOption
        icon={<FileTextIcon className="w-6 h-6 text-white" />}
        title={t('account.billingHistory')}
        description={t('account.billingHistoryDesc')}
        link={recurlyUrl || data?.data?.url || ''}
        external
      />
      <BillingOption
        icon={<GearIcon className="w-6 h-6 text-white" />}
        title={t('account.preferences')}
        description={t('account.preferencesDesc')}
        link={recurlyUrl || data?.data?.url || ''}
        external
      />
      <BillingOption
        icon={<TagIcon className="w-6 h-6 text-white" />}
        title={t('account.pricing')}
        description={t('account.pricingDesc')}
        link="/pricing"
      />
    </div>
  );
}
